'use client';

import { Button, SkeletonBar, StockStatusEnum, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { useState } from 'react';

import type { StoreData } from '@libs/common-types';

import { validateStockStatus } from '../../utils/stockStatusUtils';
import { MinimumQuantityText } from '../product-details';
import { type UseMiniPdpBopisProps, useMiniPdpBopis } from './model/use-mini-pdp-bopis';

type MiniPdpBopisDetailsProps = UseMiniPdpBopisProps &
    Readonly<{
        onOpenStoreDrawer: () => void;
    }>;

type MiniPdpBopisStoreStatusProps = Readonly<{
    isLoading: boolean;
    availableStock: number;
    minimumCaseQty: number;
    storeDetails?: StoreData['storeDetails'] | null;
}>;

function MiniPdpBopisStoreStatus({
    isLoading,
    storeDetails,
    availableStock,
    minimumCaseQty,
}: MiniPdpBopisStoreStatusProps) {
    const [showTooltip, setShowTooltip] = useState(false);
    const storeName = storeDetails?.name;
    const handleShowTooltip = () => setShowTooltip(true);
    const handleHideTooltip = () => setShowTooltip(false);

    if (isLoading) {
        return <SkeletonBar className="mini-pdp-drawer__bopis-status-skeleton" />;
    }

    if (!storeName) {
        return <TextPassage size="lg">Select a store to check availability</TextPassage>;
    }

    const stockStatus = validateStockStatus({
        stockQuantity: availableStock,
        minimumCaseQty,
    });

    let stockText = 'Out of stock';
    if (stockStatus === StockStatusEnum.InStock) stockText = 'In stock';
    if (stockStatus === StockStatusEnum.FewLeftInStock) stockText = 'Only a few left';

    return (
        <div className="mini-pdp-drawer__bopis-store-status">
            <TextPassage
                size="lg"
                className={cn({
                    'sparky-u-color-utility-success': stockStatus === StockStatusEnum.InStock,
                    'sparky-u-color-utility-error': stockStatus !== StockStatusEnum.InStock,
                })}
            >
                {stockText}
                <span className="mini-pdp-drawer__bopis-store"> at </span>
                <button
                    className="sparky-u-theme-typography-body-lg-bold mini-pdp-drawer__bopis-store button-reset"
                    data-testid="mini-pdp-bopis-store-name"
                    type="button"
                    onMouseEnter={handleShowTooltip}
                    onMouseLeave={handleHideTooltip}
                    onFocus={handleShowTooltip}
                    onBlur={handleHideTooltip}
                >
                    {storeName}
                    <span className="sparky-c-tooltip__content" style={{ display: showTooltip ? 'block' : 'none' }}>
                        {storeDetails?.streetLine1}
                        {storeDetails?.streetLine2 && (
                            <>
                                <br />
                                {storeDetails.streetLine2}
                            </>
                        )}
                        <br />
                        {storeDetails?.city}, {storeDetails?.stateProvinceAbbreviation} {storeDetails?.zipPostalCode}
                    </span>
                </button>
            </TextPassage>
        </div>
    );
}

export function MiniPdpBopisDetails({
    product,
    tailored,
    isLoading,
    isError,
    onOpenStoreDrawer,
}: MiniPdpBopisDetailsProps) {
    const { shouldRender, storeDetails, availableStock, minimumCaseQty } = useMiniPdpBopis({
        product,
        tailored,
        isLoading,
        isError,
    });

    if (!shouldRender) return null;

    return (
        <div className="mini-pdp-drawer__bopis-details" data-testid="mini-pdp-bopis-details">
            <MiniPdpBopisStoreStatus
                availableStock={availableStock}
                minimumCaseQty={minimumCaseQty}
                isLoading={isLoading}
                storeDetails={storeDetails}
            />
            <div className="delivery-details__change-store mini-pdp-drawer__bopis-change-store">
                {isLoading ? (
                    <SkeletonBar
                        data-testid="mini-pdp-change-store-skeleton"
                        className="delivery-details__change-store-skeleton mini-pdp-drawer__bopis-change-store-skeleton"
                    />
                ) : (
                    <Button
                        type="button"
                        variant="link"
                        className="delivery-details__change-store-btn"
                        text={storeDetails ? 'Change store' : 'Find a nearby store'}
                        data-testid={storeDetails ? 'mini-pdp-change-store' : 'mini-pdp-find-nearby-store'}
                        onClick={onOpenStoreDrawer}
                    />
                )}
            </div>
            <MinimumQuantityText
                minimumQuantity={minimumCaseQty}
                className="mini-pdp-drawer__bopis-min-qty"
                testId="mini-pdp-bopis-min-qty"
            />
        </div>
    );
}
